import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';
import { isToday } from 'date-fns';

import { ignoreTimezone } from '@order/utils';

import type { CheckoutPayload } from '../../Checkout.utils';

export const useCheckoutHandlers = ({
  setPayload,
  setShouldShowFutureDateModal,
}: CheckoutHandlersProps): CheckoutHandlers => {
  const handleWantedTimeSelected = useCallback(
    (wantedTime: string) => {
      const normalizedWantedTime = ignoreTimezone(wantedTime);
      const isWantedTimeToday = normalizedWantedTime
        ? isToday(normalizedWantedTime)
        : false;

      if (!isWantedTimeToday) setShouldShowFutureDateModal(true);

      setPayload((payload) => ({
        ...payload,
        wantedTime,
      }));
    },
    [setShouldShowFutureDateModal, setPayload],
  );

  const handleDropoffLocationSelected = useCallback(
    (dropoffLocationId: string | number) => {
      setPayload((payload) => ({
        ...payload,
        dropoffLocationId: String(dropoffLocationId),
      }));
    },
    [setPayload],
  );

  const handleCreditCardSelected = useCallback(
    (billingAccountId: string) => {
      setPayload((payload) => ({
        ...payload,
        billingAccountId,
      }));
    },
    [setPayload],
  );

  const handleAvailableCreditSelected = useCallback(() => {
    setPayload((payload) => ({
      ...payload,
      useCredit: !payload.useCredit,
    }));
  }, [setPayload]);

  const handleUtensilsToggle = useCallback(() => {
    setPayload((payload) => ({
      ...payload,
      includeUtensils: !payload.includeUtensils,
    }));
  }, [setPayload]);

  const handleRestaurantTip = useCallback(
    (tip: number) => {
      setPayload((payload) => ({
        ...payload,
        pickupTip: Math.round(tip),
      }));
    },
    [setPayload],
  );

  const handleDeliveryTip = useCallback(
    (tip: number) => {
      setPayload((payload) => {
        return {
          ...payload,
          deliveryOrderDetail: payload.deliveryOrderDetail && {
            ...payload.deliveryOrderDetail,
            tip: Math.round(tip),
          },
        };
      });
    },
    [setPayload],
  );

  return useMemo(
    () => ({
      handleWantedTimeSelected,
      handleDropoffLocationSelected,
      handleCreditCardSelected,
      handleAvailableCreditSelected,
      handleRestaurantTip,
      handleDeliveryTip,
      handleUtensilsToggle,
    }),
    [
      handleWantedTimeSelected,
      handleDropoffLocationSelected,
      handleCreditCardSelected,
      handleAvailableCreditSelected,
      handleRestaurantTip,
      handleDeliveryTip,
      handleUtensilsToggle,
    ],
  );
};

// ───── Types ──────────────────────────────────────────────────────

type CheckoutHandlersProps = {
  readonly setPayload: Dispatch<SetStateAction<CheckoutPayload>>;
  readonly setShouldShowFutureDateModal: (state: boolean) => void;
};

type CheckoutHandlers = {
  readonly handleWantedTimeSelected: (wantedTime: string) => void;
  readonly handleDropoffLocationSelected: (
    dropoffLocationId: string | number,
  ) => void;
  readonly handleCreditCardSelected: (billingAccountId: string) => void;
  readonly handleAvailableCreditSelected: () => void;
  readonly handleRestaurantTip: (val: number) => void;
  readonly handleDeliveryTip: (val: number) => void;
  readonly handleUtensilsToggle: () => void;
};
